import type { Category } from '../../../entities/Category';
import type { NodeBase, NodeBaseItem } from '../../../entities/Node';
import type { RootState } from '../../index';
import { MISSING_ITEMS_CATALOG } from '../../../utils/catalog/missingitems';
import { STATE_NEW, STATE_UNPUBLISHED } from '../../../utils/category/states';
import {
  getMissing,
  getUnpublished,
  getUntranslated,
} from '../../../utils/tree/indicators';
import { getNodeChildItemCount, getNodeType } from '../../../utils/tree/nodeItem';
import {
  CATALOG_TYPE_BOX,
  CATALOG_TYPE_ROOMS,
  CATALOG_TYPE_STORE,
} from '../../../utils/types/catalogTypes';
import {
  NODE_TYPE_CATALOG,
  NODE_TYPE_CATEGORY,
} from '../../../utils/types/nodeTypes';
import { selectCategoriesStatusByCatalogId } from '../../slices/indicator';

import {
  selectSelectedNodeItem,
  selectTreeDataByCatalogId,
  selectTreeDataByTreeId,
} from '../tree/tree';
import {
  createReferenceEqualSelector,
  createShallowEqualResultSelector,
  getCatalogIdFromTreeId,
} from '../utils';

const levelRestrictedCatalog = [
  CATALOG_TYPE_BOX,
  CATALOG_TYPE_ROOMS,
  CATALOG_TYPE_STORE,
] as const;

export const newCategoryEnabledSelector = createReferenceEqualSelector(
  selectSelectedNodeItem,
  getCatalogIdFromTreeId,
  selectTreeDataByTreeId,
  (
    selectedNodeItem: { [key: string]: NodeBaseItem } | Record<string, never>,
    catalogId: string | null,
    treeData: NodeBase[] | readonly [],
  ): boolean => {
    const nodeItem = catalogId ? selectedNodeItem?.[catalogId] : undefined;
    const nodeType = getNodeType(nodeItem);
    if (
      nodeItem?.node.isNew ||
      ![NODE_TYPE_CATALOG, NODE_TYPE_CATEGORY].includes(nodeType) ||
      getNodeChildItemCount(nodeItem)
    ) {
      return false;
    }

    if (
      nodeItem &&
      treeData.length > 0 &&
      'catalog' in treeData[0] &&
      treeData[0].catalog &&
      levelRestrictedCatalog.includes(
        treeData[0].catalog.catalogType as
          | typeof CATALOG_TYPE_BOX
          | typeof CATALOG_TYPE_ROOMS
          | typeof CATALOG_TYPE_STORE,
      ) &&
      nodeItem.node.level > 0
    ) {
      // only allow creation of new category on L1 for selected catalog
      return false;
    }
    return true;
  },
);

interface IndicatorBadges {
  badges: string[];
  summaries: {
    deleted?: { count: number; hasSubCategories: boolean };
    unpublished?: { count: number; hasSubCategories: boolean };
    untranslated?: { count: number; hasSubCategories: boolean };
  };
}

export const indicatorsSelector = createShallowEqualResultSelector(
  selectCategoriesStatusByCatalogId,
  selectTreeDataByCatalogId,
  (_state: RootState, _catalogId: string, category: Category) => category,
  (_state: RootState, catalogId: string) => catalogId,
  (
    categoryStatus: unknown,
    treeData: NodeBase[] | readonly NodeBase[],
    category: Category,
    catalogId: string,
  ): IndicatorBadges => {
    const publishState =
      category?.newCategory && category.publishState === STATE_UNPUBLISHED
        ? null
        : category?.publishState;

    return {
      badges: [
        category?.translateState || '',
        category?.newCategory ? STATE_NEW : '',
        publishState || '',
      ],
      summaries: {
        deleted:
          catalogId === MISSING_ITEMS_CATALOG.catalogId
            ? getMissing(treeData, category)
            : undefined,
        unpublished: getUnpublished(categoryStatus, category),
        untranslated: getUntranslated(categoryStatus, category),
      },
    };
  },
);
